import React from 'react';
import KitLandingLayout from './KitLandingLayout';

const config = {
  slug: 'inmobot',
  name: 'InmoBot',
  breadcrumb: 'Kit InmoBot',
  industry: 'corredoras y agentes inmobiliarios',
  seoTitle: 'InmoBot — Chatbot WhatsApp para Corredoras de Propiedades',
  seoDescription: 'Califica leads, agenda visitas y capta propiedades automáticamente. El asistente IA para corredores de propiedades.',
  accent: 'indigo',
  accentHex: '#4F46E5',
  heroEmoji: '🏠',
  title: 'InmoBot — tu corredora capta y agenda desde WhatsApp',
  tagline:
    'InmoBot atiende a quienes buscan comprar o arrendar, y también a dueños que quieren vender o arrendar su propiedad. Califica cada lead automáticamente y agenda la visita sin que tengas que intervenir.',
  stats: [
    { value: '24/7', label: 'atención automática' },
    { value: '2', label: 'tipos de lead detectados' },
    { value: '🔥', label: 'score automático' },
  ],
  leadTypes: [
    {
      badge: '🔍 Comprador / Arrendatario',
      title: 'Buscador',
      body: 'Quiere comprar o arrendar. El bot pregunta presupuesto, zona, tipo de propiedad, dormitorios, financiamiento y urgencia. Luego agenda la visita.',
    },
    {
      badge: '🏡 Dueño',
      title: 'Propietario',
      body: 'Quiere vender o arrendar la suya. El bot capta datos de la propiedad y agenda reunión de captación con el agente.',
    },
  ],
  features: [
    { title: 'Catálogo de propiedades', body: 'Tipo, precio, sector, dormitorios y link a fotos.' },
    { title: 'Búsqueda por filtros', body: 'Tipo, zona, precio y dormitorios directamente en WhatsApp.' },
    { title: 'Agendamiento de visitas', body: 'Disponibilidad real en Google Calendar, confirmación y recordatorio automático.' },
    { title: 'Orientación de financiamiento', body: 'Pie estimado, renta mínima y cuotas aproximadas (solo para compradores).' },
    { title: 'Notificación al corredor', body: 'Aviso inmediato con resumen cuando entra lead caliente o se agenda visita.' },
    { title: 'Temperatura del lead', highlight: true, body: '🔥 Caliente / 🌡️ Tibio / ❄️ Frío — evaluado automáticamente por comportamiento.' },
  ],
  flowSubtitle: 'Del primer mensaje al lead clasificado en 5 pasos.',
  flow: [
    { title: 'Escribe por WhatsApp', body: 'InmoBot detecta si es buscador o propietario en los primeros mensajes.' },
    { title: 'Califica con preguntas naturales', body: 'Máximo 2 por mensaje, siempre conversacional — nunca formulario.' },
    { title: 'Muestra propiedades disponibles', body: 'Ficha completa y link directo a las fotos.' },
    { title: 'Agenda visita o reunión', highlight: true, body: 'Google Calendar real, confirmación + recordatorio automático al cliente y al corredor.' },
    { title: 'El corredor recibe el lead clasificado', body: 'Datos completos + tipo (buscador/propietario) + temperatura del lead.' },
  ],
  // Special section rendered manually in the layout via `leadTypes` above.
  // We ALSO add a small explainer under features about the 3 temperatures.
  temperatures: [
    { emoji: '🔥', title: 'Caliente', body: 'Decidido, entrega datos, confirma cita para visitar, urgencia alta, presupuesto/pago resuelto.' },
    { emoji: '🌡️', title: 'Tibio', body: 'Interesado pero no listo, esperando crédito o juntar el pie, no agenda todavía.' },
    { emoji: '❄️', title: 'Frío', body: 'Curiosea, no completa datos, abandona la conversación.' },
  ],
  mockup: {
    left: {
      title: 'Leads recientes',
      subtitle: 'Últimas 24 horas',
      rows: [
        { primary: 'María Fernández · Buscador', secondary: 'Depto · Providencia · 2D · $65MM', status: 'Caliente' },
        { primary: 'Luis Mora · Propietario', secondary: 'Casa · Ñuñoa · Arriendo $850k', status: 'Confirmado' },
        { primary: 'Carla Reyes · Buscador', secondary: 'Depto · Las Condes · 3D · $95MM', status: 'Tibio' },
        { primary: 'Jorge Salinas · Buscador', secondary: 'Depto · Santiago Centro · 1D', status: 'Frío' },
      ],
    },
    right: {
      title: 'Propiedades activas',
      action: 'Ver todas',
      rows: [
        { primary: 'Depto 2D · Providencia', secondary: '$68.500.000 · 65 m²', badge: 'Venta', link: 'Ver fotos' },
        { primary: 'Casa 4D · Ñuñoa', secondary: '$850.000/mes · 180 m²', badge: 'Arriendo', link: 'Ver fotos' },
        { primary: 'Depto 3D · Las Condes', secondary: '$110.000.000 · 95 m²', badge: 'Venta', link: 'Ver fotos' },
      ],
    },
  },
  useCases: [
    'Corredores independientes',
    'Corredoras con varios agentes',
    'Captación de propiedades',
    'Gestión de múltiples carteras',
  ],
};

export default function InmoBotKitPage() {
  return (
    <>
      <KitLandingLayout config={config} />
      {/* Extra: temperatura del lead — se renderiza bajo la layout como sección fija */}
      <InmoTemperaturesSection temperatures={config.temperatures} slug={config.slug} />
    </>
  );
}

function InmoTemperaturesSection({ temperatures, slug }) {
  return (
    <section className="py-14 px-6 bg-white border-t border-gray-200" data-testid={`${slug}-temperatures`}>
      <div className="max-w-5xl mx-auto text-center">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-950 mb-3">Calificación automática del lead</h2>
        <p className="text-gray-600 max-w-2xl mx-auto mb-10">
          InmoBot analiza el comportamiento del interesado y le asigna una temperatura para que priorices bien.
        </p>
        <div className="grid md:grid-cols-3 gap-5">
          {temperatures.map((t, i) => (
            <div
              key={i}
              className="bg-gray-50 border border-gray-200 rounded-2xl p-6 text-left"
              data-testid={`${slug}-temp-${i}`}
            >
              <div className="text-3xl mb-3">{t.emoji}</div>
              <div className="text-lg font-semibold text-gray-950 mb-2">{t.title}</div>
              <p className="text-sm text-gray-600 leading-relaxed">{t.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
